import { LegacyPortalDashboardService } from "@spwn-portal/grpc";
import firebase from "firebase/app";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import useSWR from "swr";

import { useConnectClient } from "../../utility/connectWeb";

import type { ConnectError } from "@bufbuild/connect";

type IssueTicketSummary = {
  ticketId: string;
  ticketName: string;
  purchasedCount: number;
  checkedInCount: number;
  checkedInRate: number;
  redeemableCount: number;
  redeemedCount: number;
  redeemedRate: number;
};

type IssueTicketOverallSummary = {
  purchasedCount: number;
  checkedInCount: number;
  checkedInRate: number;
  redeemableCount: number;
  redeemedCount: number;
  redeemedRate: number;
};

type FetchAdminTicketResponse = {
  tickets: IssueTicketSummary[];
  updatedAt: Date;
};

//エラーの型
type FetchAdminTicketError = ConnectError | { message: string };

const FetchAdminTicketKey = "fetchAdminTicket";
const REFRESH_INTERVAL_MS = 30 * 1000; // 30秒

export const useFetchAdminTicket = () => {
  //チケットidをクエリから取得する
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const ticketIds = params.getAll("ticket_id");
  const eventTitle = params.get("event_title") ?? "";

  const auth = firebase.auth();
  const connectClient = useConnectClient(LegacyPortalDashboardService);
  const currentUserId = auth.currentUser?.uid ?? null;
  const {
    data: issuedTicketSummary,
    isLoading,
    isValidating,
    error,
    mutate,
  } = useSWR<FetchAdminTicketResponse, FetchAdminTicketError>(
    currentUserId && ticketIds.length > 0
      ? [FetchAdminTicketKey, currentUserId, ticketIds]
      : null,
    async () => {
      const response = await connectClient.getAllIssuedTicketSummary({
        ticketIds,
      });
      const tickets = response.tickets.map((ticket) => ({
        ticketId: ticket.ticketId,
        ticketName: ticket.ticketName,
        purchasedCount: ticket.purchasedCount,
        checkedInCount: ticket.checkedInCount,
        checkedInRate: roundToTwoDecimalPlaces(ticket.checkedInRate),
        redeemableCount: ticket.redeemableCount,
        redeemedCount: ticket.redeemedCount,
        redeemedRate: roundToTwoDecimalPlaces(ticket.redeemedRate),
      }));

      return {
        tickets,
        updatedAt: new Date(),
      };
    },
    {
      refreshInterval: REFRESH_INTERVAL_MS,
    }
  );
  return useMemo(() => {
    const displayTicketSummary = issuedTicketSummary?.tickets ?? [];
    const lastUpdated = issuedTicketSummary?.updatedAt ?? null;
    const overallSummary = aggregateOverallSummary(displayTicketSummary);
    return {
      displayTicketSummary,
      overallSummary,
      isLoading,
      isValidating,
      error,
      lastUpdated,
      mutate,
      eventTitle,
    };
  }, [issuedTicketSummary, isLoading, isValidating, error, mutate, eventTitle]);
};

const aggregateOverallSummary = (
  tickets: IssueTicketSummary[]
): IssueTicketOverallSummary => {
  const purchasedCount = tickets.reduce((sum, t) => sum + t.purchasedCount, 0);
  const checkedInCount = tickets.reduce((sum, t) => sum + t.checkedInCount, 0);
  const redeemableCount = tickets.reduce(
    (sum, t) => sum + t.redeemableCount,
    0
  );
  const redeemedCount = tickets.reduce((sum, t) => sum + t.redeemedCount, 0);
  return {
    purchasedCount,
    checkedInCount,
    checkedInRate:
      purchasedCount > 0
        ? roundToTwoDecimalPlaces((checkedInCount / purchasedCount) * 100)
        : 0,
    redeemableCount,
    redeemedCount,
    redeemedRate:
      redeemableCount > 0
        ? roundToTwoDecimalPlaces((redeemedCount / redeemableCount) * 100)
        : 0,
  };
};

//割合を小数点以下2桁に丸める
const roundToTwoDecimalPlaces = (num: number) => Math.round(num * 100) / 100;
